<template>
  <div class="reminder-container" id="reminder-container">
    <ThemedIcon name="close" @click="close" class="modal-close" />
    <div class="close-mobile">
      <svg
        class="closer"
        @click="close"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="4"
        viewBox="0 0 24 4"
        fill="none"
      >
        <path
          d="M2 2H22"
          stroke="#CDCDCD"
          stroke-width="3"
          stroke-linecap="round"
        />
      </svg>
    </div>
    <!-- TITULO DEL MODAL -->
    <div class="modal-header">
      <div class="titleContainer">
        <img src="@/assets/images/muni/plus-clock.svg" />
        <h1 class="h1-heroMuni">Nuevo Recordatorio</h1>
      </div>
      <div class="container-seeAll">
        <button class="actionBtn h2-heroMuni" @click="seeReminders">
          Ver Todos
        </button>
      </div>
    </div>
    <div class="modal-body">
      <div class="fields-container">
        <div class="section">
          <h2 class="h2-defaultMuni">Descripción</h2>
          <textarea
            v-model="description"
            type="text"
            class="VecinoInput BoldLetter textAreaExpanded h2-defaultMuni"
            placeholder="Detallá el comentario"
          />
        </div>
        <div class="date-container">
          <div class="section">
            <h2 class="h2-defaultMuni">Fecha:</h2>
            <PickadayComponent
              class="h2-defaultMuni"
              v-model="dateSelected"
              :min-date="new Date()"
              singleMode
              placeholder="Seleccioná una fecha"
            />
          </div>
          <div class="section">
            <h2 class="h2-defaultMuni">Hora:</h2>
            <SearchableSelect
              :isDisabledComponent="!dateSelected"
              class="h2-defaultMuni"
              :options="availableHours"
              :selected="hourSelected"
              :useLastAsPlaceholder="true"
              defaultPlaceholder="Seleccioná un horario"
              @select="
                (v) => {
                  hourSelected = v;
                }
              "
              overlayDropdown
            />
          </div>
        </div>
      </div>
      <div class="btnsSection acciones">
        <button
          @click="submit"
          class="actionBtn h2-heroMuni"
          :disabled="disabledSend"
          :class="{ deshabilitado: disabledSend }"
        >
          Agendar
        </button>
      </div>
    </div>
    <div class="modal-footer"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useToast } from "vue-toastification";
import ModalExito from "../Estados/ModalExito.vue";
import LoadingComponent from "@/components/Municipal/Modals/Carga/LoadingComponent.vue";
import SearchableSelect from "@/components/Utils/SearchableSelect.vue";
import PickadayComponent from "@/components/Utils/PickadayComponent.vue";
import LoadingMessages from "@/services/helpers/loadingMessages";
import ReminderService from "@/services/office/reminderService";
import SeeReminders from "./SeeReminders.vue";
import ThemedIcon from "@/components/Utils/ThemedIcon.vue";

const isSending = ref(false);
const loadingModalMessage = ref("");
const toast = useToast();

const emit = defineEmits(["close-modal", "open-modal"]);

const description = ref("");
const hourSelected = ref(null); // Almacenará la hora como string "HH:00"
const dateSelected = ref(null); // Almacenará el objeto Date de Pikaday

// Observa cambios en la fecha seleccionada para resetear la hora
watch(dateSelected, (newDate, oldDate) => {
  // Si la fecha cambia (y no es la carga inicial), resetea la hora
  if (newDate?.getTime() !== oldDate?.getTime()) {
    hourSelected.value = null;
  }
});

// Calcula las horas disponibles
const availableHours = computed(() => {
  if (!dateSelected.value) {
    return []; // No hay horas si no hay fecha
  }

  const hours = [];
  const now = new Date();
  const selectedDate = new Date(dateSelected.value); // Crea una copia para no mutar la prop

  let startHour = 0;

  // Comprueba si la fecha seleccionada es hoy
  const isToday =
    selectedDate.getFullYear() === now.getFullYear() &&
    selectedDate.getMonth() === now.getMonth() &&
    selectedDate.getDate() === now.getDate();

  if (isToday) {
    // Si es hoy, la hora de inicio es la siguiente hora completa
    startHour = now.getHours() + 1;
  }

  // Genera las horas desde startHour hasta 23
  for (let i = startHour; i < 24; i++) {
    // Formatea la hora con dos dígitos (ej: "09:00")
    const hourString = `${String(i).padStart(2, "0")}:00`;
    hours.push(hourString);
  }

  return hours;
});

const close = () => {
  emit("close-modal");
};

const disabledSend = computed(() => {
  return (
    isSending.value ||
    !description.value.trim() ||
    !dateSelected.value ||
    !hourSelected.value
  );
});

const getMessageForModal = (situation) => {
  const situ = LoadingMessages.Messages.find(
    (item) => item.situation === (situation ?? "")
  );
  loadingModalMessage.value = situ?.message ?? "";
};

const seeReminders = () => {
  emit("open-modal", SeeReminders, {
    size: "auto",
    position: window.innerWidth <= 768 ? "bottom" : null, 
  });
};

const submit = async () => {
  if (disabledSend.value) return;

  isSending.value = true;
  getMessageForModal("SavingReminder");
  emit("open-modal", LoadingComponent, {
    size: "auto",
    message: loadingModalMessage,
    opacity: 0.6,
    noClose: true,
  });

  try {
    // Combina fecha y hora seleccionada en un objeto Date
    const scheduledAt = new Date(dateSelected.value);
    const [hour] = hourSelected.value.split(":").map(Number); // Obtiene la hora del string "HH:00"
    scheduledAt.setHours(hour, 0, 0, 0); // Establece hora, minutos, segundos, ms

    const body = {
      description: description.value,
      scheduledAt: scheduledAt, // Envía el objeto Date combinado
    };

    // console.log("Enviando recordatorio:", body);
    const res = await ReminderService.saveReminder(body);

    emit("close-modal", 1); // Cierra el modal de carga

    if (!res?.success) {
      toast.error("Hubo un error al registrar el recordatorio.");
    } else {
      emit("open-modal", ModalExito, {
        size: "auto",
        message: {
          one: "¡Recordatorio agendado",
          two: "con éxito!",
        },
        noClose: true,
      });

      await new Promise((resolve) => setTimeout(resolve, 3000));
      emit("close-modal", 1); // Cierra ModalExito
      emit("close-modal"); // Cierra este modal
    }
  } catch (error) {
    console.error("Error al guardar recordatorio:", error);
    toast.error("Ocurrió un error inesperado.");
    emit("close-modal", 1); // Asegura cerrar el loading en caso de error
  } finally {
    isSending.value = false;
  }
};
</script>

<style scoped>
@import "@/components/Utils/css/form-styles.css";
.reminder-container {
  display: flex;
  flex-direction: column;
  /* height: 100%; */
  overflow: hidden;
  position: relative;
  padding: 0;
  height: 83.472vh;
  width: 51vw;
}
.modal-header {
  padding: 4.96vh 3.65vw 0.82vh 3.65vw;
  gap: 3.29vh;
  display: flex;
  flex-direction: column;
  width: 100%;
}
.modal-header img {
  width: 1.32vw;
}
.container-seeAll {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}
.container-seeAll button {
  width: fit-content;
  padding: 0.82vh 0.81vw;
}
.close-mobile {
  display: none;
  visibility: collapse;
}
.closer {
  width: 6.67vw;
}
.modal-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0rem 3.65vw 0rem 3.65vw;
  overflow-y: auto;
}

.fields-container {
  display: flex;
  flex-direction: column;
  gap: 4.11vh;
}

.section {
  display: flex;
  flex-direction: column;
  height: fit-content;
  gap: 0.82vh;
  width: 100%;
}

.date-container {
  display: flex;
  flex-direction: row;
  gap: 1.1vw;
  width: 100%;
}

.textAreaExpanded {
  min-height: 13.51vh;
  height: auto;
  resize: none;
  padding: 1.63vh 1.46vw;
  background: var(--textarea);
}

textarea::placeholder {
  color: var(--text-primary);
}

.modal-close {
  position: fixed;
  right: 1.464vw;
  top: 2.838vh;
  background: none;
  border: none;
  font-size: calc(18px + (100vw - 1366px) * 0.01);
  cursor: pointer;
}

.titulo-modal {
  gap: var(--gap10);
}
.content {
  gap: 2.504vh;
  padding: 5.008vh 0 10.017vh 0;
}

.acciones {
  gap: var(--gapH24);
}

[data-theme="light"] .acciones {
  box-shadow: 0 -20px 30px 0px rgba(255, 255, 255, 1);
}

[data-theme="dark"] .acciones {
  box-shadow: 0 -20px 30px 0px rgba(0, 0, 0, 0);
}

.acciones button.deshabilitado {
  background: #cccccc;
  cursor: not-allowed;
  opacity: 0.6;
  color: var(--bg-popup);
}

.acciones button:active,
.acciones button:focus,
.acciones button:hover {
  box-shadow: none;
  outline: none;
  filter: none;
}

.btnsSection {
  margin-top: 1rem;
  /* padding-bottom: 1.88rem; */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 1.56rem;
}

.actionBtn {
  width: 12rem;
  border-radius: 0.625rem;
  border: none;
  background-color: var(--text-primary);
  padding: 0.3rem;
  color: var(--bg-popup);
}

.modal-footer {
  min-height: 1.88rem;
}

:deep(.inputMuniV2) {
  display: flex;
  padding: 10px 20px;
  align-items: flex-start;
  gap: 10px;
  align-self: stretch;
  border-radius: 10px;
  border: none;
  outline: none;
  width: 100%;
  height: 4.94vh;
}

:deep(.h2-defaultMuni .inputMuniV2) {
  /* Quita los estilos fijos del hijo */
  height: auto;
  font-size: inherit; /* Hereda explícitamente */
  line-height: inherit;

  /* Fuerza al input a heredar la fuente del wrapper */
  font-family: inherit;
  font-weight: inherit;
  color: inherit;
  
  /* Restaura el padding/altura mínima */
  min-height: 30px;
  padding: 5px 1.2rem; /* Mantiene el padding original */
}

:deep(.inputMuniV2)::placeholder {
  color: var(--text-primary);
}

:deep(.datepicker-container .display-input) {
  background: var(--textarea);
  height: 4.94vh;
  padding: 10px 20px;
  border-radius: 10px;
}
 
:deep(.datepicker-container .display-input)::placeholder {
  color: var(--text-primary);
}

/* -------------------------------------------------------------------------- */
/* RESPONSIVE MOBILE STYLES                         */
/* -------------------------------------------------------------------------- */

/* Define el punto de corte (breakpoint) para móvil, por ejemplo, 768px */
@media (max-width: 768px) {
  /* Contenedor principal del modal */
  .reminder-container {
    /* Fija la altura total del modal, aprovechando el espacio desde abajo */
    height: 80vh;
    width: 100vw;

    /* Asegura que el contenido interno pueda scrollear */
    overflow: hidden; /* Esto ya estaba, pero lo confirmamos */
  }

  /* El cuerpo del modal (modal-body) es el que debe tener el scroll */
  .modal-body {
    /* Permite que el cuerpo ocupe el espacio restante y scrollee */
    flex-grow: 1;
    overflow-y: auto; /* Habilita el scroll vertical */

    /* Ajuste de padding para pantallas más pequeñas */
    padding: 0rem 1rem 0rem 1rem; /* Reducimos el padding horizontal */

    /* Aseguramos que los elementos dentro no se desborden horizontalmente */
    width: 100%;
    box-sizing: border-box;
    scrollbar-width: none;
  }

  /* El encabezado y el pie de página deben tener altura fija y no scrollear */
  .modal-header,
  .modal-footer {
    flex-shrink: 0; /* Impide que se achiquen */
  }

  .modal-header {
    padding: 2.04vh; /* Ajuste de padding para móvil */
    padding-top: 1.53vh;
    flex-direction: row;
    white-space: nowrap;
  }
  .modal-header img {
    width: 5vw;
  }
  .container-seeAll button {
    padding: 0.64vh 2.78vw;
  }

  .textAreaExpanded {
    min-height: 8.42vh;
    padding: 10px 20px;
  }
  .date-container {
    flex-direction: column;
    gap: 4.11vh;
  }

  .close-mobile {
    visibility: visible;
    height: 2.29vh;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  /* Ajustes específicos de elementos internos */
  .section {
    gap: 0.5rem; /* Reduce el espacio entre secciones */
  }

  /* Reubicamos el botón de cerrar para que no se pise */
  .modal-close {
    /* right: 1rem;
    top: 1rem; */
    visibility: collapse;
  }

  .btnsSection {
    margin-top: 0rem;
    padding: 1.25rem 0rem 0rem 0rem;
    box-shadow: none;
    /* Fuerza a los botones a ser más grandes y apilados si hay poco espacio */
    flex-direction: column;
    gap: 1.27vh;
  }

  .modal-footer {
    min-height: 2.55vh;
  }

  .actionBtn {
    width: 100%;
    padding: 0.96vh;
  }

  :deep(.datepicker-container .display-input) {
    height: 4.59vh;
  }

  /* animacion en mobile */
  @keyframes slideUp {
    from {
      transform: translateY(100%) translateX(-50%);
      opacity: 0;
    }
    to {
      transform: translateY(0) translateX(-50%);
      opacity: 1;
    }
  }
  @keyframes slideDown {
    /* Animación de salida */
    from {
      transform: translateY(0) translateX(-50%);
      opacity: 1;
    }
    to {
      transform: translateY(100%) translateX(-50%);
      opacity: 0;
    }
  }

  :global(.modal-container[data-component="NewReminder"].opening) {
    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    opacity: 0;
  }

  :global(.modal-container[data-component="NewReminder"].closing) {
    animation: slideDown 0.3s cubic-bezier(0.7, 0, 0.84, 0) forwards;
    pointer-events: none !important;
  }
}
</style>
